import React, { ChangeEvent, createRef } from 'react';
import { Icon, Message } from 'semantic-ui-react';
import { inject, observer } from 'mobx-react';
import { AuthServiceInterface } from '../services/AuthService';
import { Redirect, Link } from 'react-router-dom';
import AuthPage from '../components/auth/AuthPage';
import TagManagerService from '../services/TagManagerService';

export interface AuthProps {
  auth?: AuthServiceInterface;
  location?: { state: { email: string } };
}

interface LoginPageState {
  errorMessage: string;
  email: string;
  password: string;
  loading: boolean;
  hidePassword: boolean;
  isEmailValid: boolean;
  isTypingEmail: boolean;
  isAutofilled: boolean;
}

@inject('auth')
@observer
export default class LoginPage extends React.Component<AuthProps, LoginPageState> {
  reviewLink = 'https://g.page/r/CX9k8-vnxZ2sEBM/review';
  googleLogo = '/img/google-color.svg';
  emailRef = createRef<HTMLInputElement>();
  passwordRef = createRef<HTMLInputElement>();
  autofillTimer: ReturnType<typeof setInterval> | null = null;
  state: LoginPageState = {
    errorMessage: '',
    email: '',
    password: '',
    loading: false,
    hidePassword: true,
    isEmailValid: true,
    isTypingEmail: false,
    isAutofilled: false
  }

  async componentDidMount(): Promise<void> {
    if (typeof this.props.location !== 'undefined') {
      const email = this.props.location.state ? this.props.location.state.email : '';
      this.setState({ email });
    }
    this.startAutofillDetection();
  }

  componentWillUnmount(): void {
    if (this.autofillTimer) clearInterval(this.autofillTimer);
  }

  handleAutoFill = (e: React.AnimationEvent<HTMLInputElement>): void => {
    if (e.animationName === 'onAutoFillStart') {
      this.setState({ isAutofilled: true });
      if (this.autofillTimer) clearInterval(this.autofillTimer);
    }
  }

  private startAutofillDetection(): void {
    let attempts = 0;
    this.autofillTimer = setInterval(() => {
      attempts++;
      const emailEl = this.emailRef.current;
      const passwordEl = this.passwordRef.current;

      if (emailEl && passwordEl) {
        try {
          if (emailEl.matches(':-webkit-autofill') || passwordEl.matches(':-webkit-autofill')) {
            this.setState({ isAutofilled: true });
            if (this.autofillTimer) clearInterval(this.autofillTimer);
            return;
          }
        } catch (e) { /* not supported in this browser */ }

        const emailVal = emailEl.value?.trim();
        const passwordVal = passwordEl.value?.trim();
        if (emailVal && emailVal !== this.state.email && passwordVal && passwordVal !== this.state.password) {
          this.setState({
            email: emailVal,
            password: passwordVal,
            isEmailValid: !!emailVal.match(/\S+@\S+\.\S+/)
          });
          if (this.autofillTimer) clearInterval(this.autofillTimer);
          return;
        }
      }

      if (attempts >= 30) {
        if (this.autofillTimer) clearInterval(this.autofillTimer);
      }
    }, 200);
  }

  private async login(): Promise<void> {
    const email = (this.emailRef.current?.value || this.state.email).trim();
    const password = (this.passwordRef.current?.value || this.state.password).trim();
    this.setState({ loading: true });
    if (this.props.auth) {
      try {
        await this.props.auth.login(email, password);
        this.setState({ errorMessage: '' });
        TagManagerService.logLoginEvent(email);
      } catch (e) {
        this.setState({ errorMessage: e.message, loading: false });
      }
    }
  }

  async validateEmailInput(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    this.setState({ isTypingEmail: false });
    const valid = e.target.value.match(/\S+@\S+\.\S+/);
    if (valid?.length) {
      this.setState({ isEmailValid: true });
    } else {
      this.setState({ isEmailValid: false });
    }
  }

  onEmailChange(e: React.ChangeEvent<HTMLInputElement>): void {
    this.setState({ isTypingEmail: true, isAutofilled: false });
    const valid = e.target.value.trim().match(/\S+@\S+\.\S+/);
    if (valid?.length) {
      this.setState({ isEmailValid: true });
    } else {
      this.setState({ isEmailValid: false });
    }
    this.setState({ email: e.target.value.trim() });
  }

  handleKeyPress = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && this.state.email && this.state.password && this.state.isEmailValid && !this.state.loading) {
      this.login();
    }
  }

  render(): React.ReactNode {
    if (this.props.auth?.user?.id) {
      return <Redirect to="/" />;
    }

    return (
      <AuthPage pageTitle="Login - PAYearned">
        <div className="login-container">
          <h1 className="login-title">Login</h1>
          <p className="login-subtitle">
            Don't have an account? <Link to="/signup">Sign up FREE</Link>
          </p>

          <div className="auth-form-card">
            {this.state.errorMessage && (
              <div className="auth-error-message">
                {this.state.errorMessage}
              </div>
            )}

            {!this.state.isEmailValid && !this.state.isTypingEmail && (
              <div className="auth-error-message">
                Invalid email address
              </div>
            )}

            <div className="auth-form-group">
              <label className="auth-form-label">Email Address</label>
              <input
                ref={this.emailRef}
                type="email"
                className={`auth-form-input ${!this.state.isEmailValid && !this.state.isTypingEmail ? 'error' : ''}`}
                placeholder="JohnSmith@email.com"
                value={this.state.email}
                onChange={(e: ChangeEvent<HTMLInputElement>): void => this.onEmailChange(e)}
                onFocus={(): void => this.setState({ isEmailValid: true })}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>): Promise<void> => this.validateEmailInput(e)}
                onKeyPress={this.handleKeyPress}
                onAnimationStart={this.handleAutoFill}
              />
            </div>

            <div className="auth-form-group">
              <label className="auth-form-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  ref={this.passwordRef}
                  type={this.state.hidePassword ? 'password' : 'text'}
                  className="auth-form-input"
                  placeholder="••••••••••"
                  value={this.state.password}
                  onChange={(e: ChangeEvent<HTMLInputElement>): void => this.setState({ password: e.target.value.trim(), isAutofilled: false })}
                  onKeyPress={this.handleKeyPress}
                  onAnimationStart={this.handleAutoFill}
                />
                <Icon
                  name={this.state.hidePassword ? 'eye slash' : 'eye'}
                  link
                  onClick={(): void => this.setState({ hidePassword: !this.state.hidePassword })}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-63%)',
                    color: !this.state.hidePassword ? '#0085c8' : '#999',
                    cursor: 'pointer'
                  }}
                />
              </div>
            </div>

            <button
              className="auth-form-button"
              disabled={(!this.state.isAutofilled && (!this.state.email || !this.state.password || !this.state.isEmailValid)) || this.state.loading}
              onClick={(): Promise<void> => this.login()}
            >
              {this.state.loading ? 'Logging in...' : 'Login'}
            </button>

            <Link to="/forgot-password" className="auth-form-link">
              Forgot password?
            </Link>
          </div>
          <div className="google-review-div">
            <img src={this.googleLogo} alt="Google" width="30" />            
            <a 
              href={this.reviewLink} target="_blank" 
              rel="noopener noreferrer" 
              className="google-review"
            >
              Leave a Google Review
            </a>
          </div>
        </div>
      </AuthPage>
    );
  }
}
